/**
 * RecentActivity — compact feed of recent events (sparked, verified,
 * forked, etc.). Lives in the Vault's mission-control sidebar.
 */

import { useTranslation } from 'react-i18next';

import type { ActivityEvent } from '@/api/types';
import { useLocaleStore } from '@/stores/localeStore';
import { formatRelativeTime } from '@/utils/format';

export function RecentActivity({ events }: { events: ActivityEvent[] }) {
  const { t } = useTranslation('vault');
  const locale = useLocaleStore((s) => s.locale);

  if (events.length === 0) {
    return (
      <p className="rounded-md border border-dashed border-border bg-surface p-4 text-sm text-ink-muted">
        {t('activity.empty')}
      </p>
    );
  }

  const describe = (ev: ActivityEvent): string => {
    switch (ev.kind) {
      case 'sparked':
        return t('activity.sparked', { name: ev.name });
      case 'verified':
        return ev.ok
          ? t('activity.verified_ok', { name: ev.name })
          : t('activity.verified_fail', { name: ev.name });
      case 'executed':
        return t('activity.executed', { name: ev.name });
      case 'forked':
        return t('activity.forked_by', { name: ev.name, by: ev.by });
      case 'published':
        return t('activity.published', { name: ev.name });
      case 'revenue_in':
        return t('activity.revenue_in', { name: ev.name, amount: ev.amount_ils });
    }
  };

  return (
    <ul className="divide-y divide-border rounded-lg border border-border bg-surface">
      {events.map((ev, i) => (
        <li key={i} className="flex items-start gap-3 px-4 py-3">
          <EventGlyph kind={ev.kind} />
          <div className="flex-1 min-w-0">
            <p className="truncate text-sm text-ink">{describe(ev)}</p>
            <p className="mt-0.5 text-[11px] text-ink-subtle">
              {formatRelativeTime(ev.at.iso, locale)}
            </p>
          </div>
        </li>
      ))}
    </ul>
  );
}

function EventGlyph({ kind }: { kind: ActivityEvent['kind'] }) {
  const glyph = KIND_TO_GLYPH[kind];
  return (
    <span
      aria-hidden="true"
      className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-surface-raised text-ink-muted"
    >
      {glyph}
    </span>
  );
}

const KIND_TO_GLYPH: Record<ActivityEvent['kind'], string> = {
  sparked: '⚡',
  verified: '✓',
  executed: '▶',
  forked: '⎇',
  published: '↗',
  revenue_in: '$',
};
